import { useEffect, useRef } from "react";

// Cloudflare's official "always passes" TEST sitekey — works on ANY domain.
const TEST_SITE_KEY = "1x00000000000000000000AA";
// Production sitekey is baked in as the default so a fresh VPS deploy works even
// when the (gitignored) frontend .env is absent. It stays overridable via env.
const PROD_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "0x4AAAAAAENUkp5_3XjotPs7";

// The production sitekey is locked to the live domain (maxlive88.com) in the
// Cloudflare dashboard, so it throws error 110200 ("domain not allowed") on the
// Emergent preview URL / localhost. On those non-production hosts we transparently
// fall back to Cloudflare's domain-agnostic TEST key so the flow keeps working,
// while the live site still uses the real production key.
function _resolveSiteKey() {
  if (typeof window === "undefined") return PROD_SITE_KEY;
  const host = (window.location.hostname || "").toLowerCase();
  const isNonProd =
    host.includes("emergentagent.com") ||
    host === "localhost" ||
    host === "127.0.0.1" ||
    host.endsWith(".local");
  return isNonProd ? TEST_SITE_KEY : PROD_SITE_KEY;
}

const SITE_KEY = _resolveSiteKey();

/**
 * Cloudflare Turnstile widget (bot / fake-account protection).
 * The script tag lives in public/index.html; we render explicitly here.
 *
 * Props:
 *  - onVerify(token): called with the token on success, and "" on expire/error
 *  - resetSignal: change this number to force the widget to reset (e.g. after a
 *    failed submit — Turnstile tokens are single-use)
 */
export function TurnstileWidget({ onVerify, resetSignal = 0, theme = "dark", className = "" }) {
  const ref = useRef(null);
  const widgetId = useRef(null);
  const onVerifyRef = useRef(onVerify);
  onVerifyRef.current = onVerify;

  useEffect(() => {
    let cancelled = false;
    let poll;
    const render = () => {
      if (cancelled || !window.turnstile || !ref.current || widgetId.current !== null) return;
      try {
        widgetId.current = window.turnstile.render(ref.current, {
          sitekey: SITE_KEY,
          theme,
          action: "register",
          callback: (t) => onVerifyRef.current?.(t),
          "expired-callback": () => onVerifyRef.current?.(""),
          "error-callback": () => onVerifyRef.current?.(""),
        });
      } catch {
        /* ignore double-render */
      }
    };
    if (window.turnstile) render();
    else poll = setInterval(() => { if (window.turnstile) { clearInterval(poll); render(); } }, 120);
    return () => {
      cancelled = true;
      if (poll) clearInterval(poll);
      try { if (widgetId.current !== null) window.turnstile?.remove(widgetId.current); } catch { /* ignore */ }
      widgetId.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (resetSignal && widgetId.current !== null && window.turnstile) {
      try { window.turnstile.reset(widgetId.current); onVerifyRef.current?.(""); } catch { /* ignore */ }
    }
  }, [resetSignal]);

  return <div ref={ref} className={className} data-testid="turnstile-widget" />;
}

export default TurnstileWidget;
